import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Button, Col, Container, FormControl, InputGroup, Row, Image } from 'react-bootstrap';
import Navbar from 'react-bootstrap/Navbar';
import OverlayTrigger from 'react-bootstrap/OverlayTrigger';
import Popover from 'react-bootstrap/Popover';
import ListGroup from 'react-bootstrap/ListGroup';

import FontAwesome from 'react-fontawesome'
import { isEmpty, difference, filter, union, includes, pickBy } from 'lodash';
import moment from 'moment';

import ProjectsTable from '../../components/ProjectsTable';
import CurrentStepsStatisticsScreen from '../CurrentStepsStatisticsScreen';
import MultiselectFilter from '../../components/MultiselectFilter';
import DateFilter from '../../components/DateFilter';

import { fetchTransitionsDat, fetchTransitionsJson } from '../../actions/transitions';
import { fetchRightsDat, fetchRightsJson } from '../../actions/rights';
import { fetchAdminStatsDat, fetchAdminStatsJson } from '../../actions/adminStats';
import { fetchProjectsInfo } from '../../actions/projectsInfo';
import { fetchLastUpdateInfo } from '../../actions/lastUpdate';

import { currentStepsStatisticsSelector } from '../../reducers/transitions/transitionsReducer';
import { projectsInfoSelector } from '../../reducers/projectsInfo/projectsInfoReducer';
import { usersExcelSelector } from '../../reducers/trt/trtReducer';
import { convertUnits, applyFilters, applyMultiselect, generateMultiselectOptions, invertObject, applySelectedDate, convertDate } from '../../utils/common';
import auth from '../../utils/auth';

import { PROJECT_STATUS_ARRAY, PROJECTS_TABLE_HEADERS, JSON_KEYS } from '../../constants';
import AUTH_LOGOUT_URL from '../../constants/auth';
import {GLOBAL_ADMIN_STATS_URL} from '../../constants/urls';

import './index.css';
import StatisticsByUser from '../StatisticsByUser';
import StatisticsByProject from '../StatisticsByProject';
import ResourcesByProject from '../ResourcesByProject';

import AdminStatisticsScreen from '../AdminStatisticsScreen';
import {
  adminStatisticsSelector,
  adminStatisticsWithTimePeriodSelector
} from '../../reducers/adminStats/adminStatsReducer';
import { fetchUsername } from '../../actions/auth';
import { getImportDataClient, getImportDataClientWithTimePeriod } from '../../actions/trt';

import time from '../../utils/time';
import excel from '../../utils/excel';

const MainScreen = () => {
  const [ t ] = useTranslation();

  const dispatch = useDispatch();
  const [inputValue, setInputValue] = useState("");
  const [showLocked, setShowLocked] = useState(true);
  const [sortOrder, setSortOrder] = useState("ascending");
  const [statisticsSelection, setStatisticsSelection] = useState([]);
  const [showUsersAndRightsHelpButton] = useState(true);
  const [multiselectedStatus, setMultiselectedStatus] = useState([]);
  const [multiselectedLeader, setMultiselectedLeader] = useState([]);
  const [multiselectedYear, setMultiselectedYear] = useState([]);
  const [multiselectedType, setMultiselectedType] = useState([]);
  const [multiselectedRights, setMultiselectedRights] = useState([]);
  const [multiselectedUsers, setMultiselectedUsers] = useState([]);
  const [statisticsUsers, setStatisticsUsers] = useState([]);
  const [selectedDate, setSelectedDate] = useState({startDate: '', endDate: ''});
  const [statisticsDate, setStatisticsDate] = useState({startDate: '', endDate: ''});
  const [adminStatsDate, setAdminStatsDate] = useState({startDate: '', endDate: ''});
  const [trtDate, setTrtDate] = useState({startDate: '', endDate: ''});
  const [selectedProjects, setSelectedProjects] = useState([]);

  const rightsDat = useSelector(state => state.rights.rightsDat);
  const rightsJson = useSelector(state => state.rights.rightsJson);
  const projectsInfo = useSelector(projectsInfoSelector);
  const lastUpdateInfo = useSelector(state => state.updateInfo.lastUpdate);
  const projectPaths = useSelector(state => state.transitions.transitionsJson.projects);
  const username = useSelector(state => state.auth.username);
  const trtExcelData = useSelector(usersExcelSelector);

  const rightsValueId = useMemo(() => invertObject(rightsJson.rights), [rightsJson]);
  const usersValueId = useMemo(() => {
    // eslint-disable-next-line no-unused-vars
    const usersForDisplay = pickBy(rightsJson.users, (value, key) => !includes(value, 'SvnServerSideKickUser'));
    return invertObject(usersForDisplay);
  }, [rightsJson]);

  // dropdown options
  const dropdownData = useMemo(() => generateMultiselectOptions(projectsInfo.projects), [projectsInfo]);
  const dropdownRights = useMemo(() => Object.keys(rightsValueId).sort(), [rightsValueId]);
  const dropdownUsers = useMemo(() => Object.keys(usersValueId).sort(), [usersValueId]);

  // filters
  const lockedFilter = data => {
    if (!showLocked) return data;
    return data.filter(project => {
      return project.status !== "Locked";
    });
  };

  const searchFilter = data => {
    const searchTerm = inputValue.toLowerCase();
    return data.filter(project => {
      return (!isEmpty(project.name) && project.name.toLowerCase().indexOf(searchTerm) !== -1) ||
      (!isEmpty(project.leader) && project.leader.toLowerCase().indexOf(searchTerm) !== -1) ||
      (!isEmpty(project.type) && project.type.toLowerCase().indexOf(searchTerm) !== -1) ||
      (!isEmpty(project.year) && project.year.toLowerCase().indexOf(searchTerm) !== -1);
    });
  };

  const changeSortOrder = () => {
    setSortOrder(sortOrder === "ascending" ? "descending" : "ascending");
  };

  const rightsAndUsersFilter = useCallback(() => {
    if (!multiselectedRights.length || !multiselectedUsers.length) return [];

    const rightIds = multiselectedRights.map(right => rightsValueId[right]);
    const userIds = multiselectedUsers.map(user => usersValueId[user]);

    const projectPathIds = rightsDat.reduce(
      // second parameter is destructured row
      (pathArray, [user, projectPath, rightCode, , hasRootRight]) => {
        if (hasRootRight) {
          if (userIds.includes(user.toString()) && rightIds.includes(rightCode.toString())) {
            if(!pathArray.includes(projectPath)) {
              pathArray.push(projectPath)
            }
          }
        }
        return pathArray;
      }, []
    );

    if (rightIds && !projectPathIds.length) return ['no results'];
    // swap projectPath ids with names
    return projectPathIds.map(projectPathId => projectPaths[projectPathId]);
  }, [multiselectedRights, multiselectedUsers, usersValueId, rightsValueId, projectPaths, rightsDat]);

  const selectFilter = useCallback(data => (
    data.filter(project => (selectedProjects.find(selectedProject => project.path === selectedProject.path)))
  ), [selectedProjects]);

  const sortByProjectName = (a, b, sortMode) => {
    if (sortMode === "descending") {
      return (`${  b.name}`).localeCompare(a.name, 'hr');
    }
    return (`${  a.name}`).localeCompare(b.name, 'hr');
  };

  const sortByDueDate = (a, b, sortMode) => {
    if (sortMode === "descending") {
      return (new Date(b.due_date).getTime() || -Infinity) - (new Date(a.due_date).getTime() || -Infinity);
    }
    return (new Date(a.due_date).getTime() || -Infinity) - (new Date(b.due_date).getTime() || -Infinity);
  };

  const sortByDiskUsage = (a, b, sortMode) => {
    if (sortMode === "descending") {
      // eslint-disable-next-line radix
      return parseInt(b.sidekick.disk_usage) - parseInt(a.sidekick.disk_usage);
    }
    // eslint-disable-next-line radix
    return parseInt(a.sidekick.disk_usage) - parseInt(b.sidekick.disk_usage);
  };

  const sortMethods = {
    name: sortByProjectName,
    due_date: sortByDueDate,
    disk_usage: sortByDiskUsage,
  };

  const [sortMethod, setSortMethod] = useState(() => sortByProjectName);

  const selectedFilters = useMemo(() => (
      !!(multiselectedStatus.length ||
        multiselectedLeader.length ||
        multiselectedYear.length ||
        multiselectedType.length ||
        multiselectedRights.length ||
        multiselectedUsers.length ||
        selectedDate.startDate.length ||
        selectedDate.endDate.length)
    ), [multiselectedStatus, multiselectedLeader, multiselectedYear, multiselectedType, multiselectedRights, multiselectedUsers, selectedDate]
  );

  const removeFilters = useCallback(() => {
    setInputValue("");
    setMultiselectedStatus([]);
    setMultiselectedLeader([]);
    setMultiselectedYear([]);
    setMultiselectedType([]);
    setMultiselectedRights([]);
    setMultiselectedUsers([]);
    setSelectedDate({startDate: '', endDate: ''});
    setSelectedProjects([]);
    setSortMethod(() => sortByProjectName);
    setSortOrder("ascending");
  }, [
    setInputValue,
    setMultiselectedStatus,
    setMultiselectedLeader,
    setMultiselectedYear,
    setMultiselectedType,
    setMultiselectedRights,
    setMultiselectedUsers,
    setSelectedDate,
    setSelectedProjects,
    setSortMethod,
    setSortOrder,
  ]);

  const filterFunctions = [
    projects => lockedFilter(projects),
    projects => searchFilter(projects),
    projects => applyMultiselect(projects, multiselectedStatus, PROJECTS_TABLE_HEADERS.STATUS),
    projects => applyMultiselect(projects, multiselectedType, PROJECTS_TABLE_HEADERS.TYPE),
    projects => applyMultiselect(projects, multiselectedLeader, PROJECTS_TABLE_HEADERS.LEADER),
    projects => applyMultiselect(projects, multiselectedYear, PROJECTS_TABLE_HEADERS.YEAR),
    projects => applyMultiselect(projects, rightsAndUsersFilter(), JSON_KEYS.PATH),
    projects => applySelectedDate(projects, selectedDate, PROJECTS_TABLE_HEADERS.DUE_DATE),
  ];

  // filtered projects
  const projects = useMemo(() => applyFilters(
      projectsInfo.projects,
      filterFunctions,
      sortMethod,
      sortOrder
  ), [
      projectsInfo,
      filterFunctions,
      sortMethod,
      sortOrder]
  );

  // statistics
  const projectsForStatistics = useMemo(() => selectFilter(projects), [projects, selectFilter]);
  const currentStepsStatistics = useSelector(currentStepsStatisticsSelector(projectsForStatistics.map(project => project.path)));
  const clientName = auth.getUsernameAndClient(username).client;
  const adminStatistics = {
    [clientName]: useSelector(adminStatisticsSelector),
  };
  const adminStatisticsTimePeriod = {
    [clientName]: useSelector(adminStatisticsWithTimePeriodSelector(
        adminStatsDate.startDate,
        adminStatsDate.endDate
    )),
  };

  const selectProject = project => {
    setSelectedProjects(prevSelectedProjects => union(prevSelectedProjects, [project]));
  };

  const deselectProject = project => {
    const newSelectedProjects = filter(selectedProjects, selectedProject => selectedProject.path !== project.path);
    setSelectedProjects(newSelectedProjects);
  };

  const selectAllProjects = () => {
    setSelectedProjects(prevSelectedProjects => union(prevSelectedProjects, projects));
  };

  const deselectAllProjects = () => {
    setSelectedProjects(prevSelectedProjects => difference(prevSelectedProjects, projectsForStatistics));
  };

  // choose statistics object
  const STATISTICS = useMemo(() => ({
    currentStep: t('selectStatistics.currentSteps'),
    byUser: t('selectStatistics.byUser'),
    byProject: t('selectStatistics.byProject'),
    byResource: t('selectStatistics.byResource'),
    general: t('selectStatistics.general'),
    trt: t('selectStatistics.trt'),
  }), [t]);

  // table headers
  const headers = [
    t('projectsTable.name'),
    <MultiselectFilter
      name={t('projectsTable.status')}
      dropdownData={PROJECT_STATUS_ARRAY}
      multiselectedData={multiselectedStatus}
      setMultiselectedData={setMultiselectedStatus}
    />,
    <MultiselectFilter
      name={t('projectsTable.type')}
      dropdownData={dropdownData.type}
      multiselectedData={multiselectedType}
      setMultiselectedData={setMultiselectedType}
    />,
    <MultiselectFilter
      name={t('projectsTable.leader')}
      dropdownData={dropdownData.leader}
      multiselectedData={multiselectedLeader}
      setMultiselectedData={setMultiselectedLeader}
    />,
    <MultiselectFilter
      name={t('projectsTable.year')}
      dropdownData={dropdownData.year}
      multiselectedData={multiselectedYear}
      setMultiselectedData={setMultiselectedYear}
    />,
    <DateFilter
      name={t('projectsTable.due_date')}
      selectedDate={selectedDate}
      setSelectedDate={setSelectedDate}
    />,
    t('projectsTable.path'),
    t('projectsTable.disk_usage')
  ];

  useEffect(() => {
    dispatch(fetchTransitionsDat());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchTransitionsJson());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchRightsDat());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchRightsJson());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAdminStatsDat());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchAdminStatsJson());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchProjectsInfo());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchLastUpdateInfo());
  }, [dispatch]);

  useEffect(() => {
    dispatch(fetchUsername());
  }, [dispatch]);

   useEffect(() => {
     const { startDate, endDate } = trtDate;
     if (startDate && endDate) {
       dispatch(getImportDataClientWithTimePeriod(
           moment(startDate).format('YYYY-MM-DD'),
           moment(endDate).format('YYYY-MM-DD'),
       ))
     }
     if (!startDate && !endDate) {
       dispatch(getImportDataClient());
     }
  }, [dispatch, trtDate]);

  useEffect(() => {
    if (!multiselectedUsers.length) {
      setMultiselectedRights([])
    }
  }, [multiselectedUsers, setMultiselectedRights]);

  const popover = (type) => {
    let popoverText = '';

    switch (type) {
      case 'diskUsage':
        popoverText = t("filters.diskUsageTooltip");
        break;

      case 'userRights':
        popoverText = t("filters.usersAndRightsTooltip");
        break;

      case 'statsUserAndProject':
        popoverText = t("filters.statsByUserAndProject");
        break;
    
      default:
        break;
    }
    
    return (
      <Popover id="popover-basic">
        <Popover.Content dangerouslySetInnerHTML={{__html: popoverText}} />
      </Popover>
    );
  }; 

  const usersAndRightsHelpButtonClass = showUsersAndRightsHelpButton
      ? 'help-button' : 'help-button help-button-invisible';

  const showFilters = (name, filters) => (
    !isEmpty(filters[0]) ?
      <ListGroup.Item>
        <h5 style={{display: "inline"}}>{t(name)}:</h5>
        {filters.map(f =>
          <span key={f} className="badge badge-primary text-wrap filter-badge"> {f} </span>
        )}
      </ListGroup.Item> : null
  );

  const { resource_properties: resources } = useSelector(projectsInfoSelector);

  const trtInfoFileName = `${t('selectStatistics.trtInfo.importsInfoExcelFileName')}-${time.convertDatetimeToLocal(new Date())}`;
  const trtInfoFileNameWithTimePeriod = (startDate, endDate) =>
      `${t('selectStatistics.trtInfo.importsInfoExcelFileName')}-${moment(startDate).format('YYYY-MM-DD')} ${moment(endDate).format('YYYY-MM-DD')}`;

  return (
    <div>
      <Navbar >
        <Navbar.Brand href="#"><Image src="favicon.png" className="navbar-brand-icon" rounded />{t('navbar.brand')} {global.appVersion}</Navbar.Brand>
          <Navbar.Toggle />
          <Navbar.Collapse className="justify-content-end navigation-buttons">
            <Row >
              {auth.canAccessGlobalAdminStats(username) &&
                <Col md="8">
                  <Button
                      variant="outline-dark"
                      href={GLOBAL_ADMIN_STATS_URL}>
                    {t('navbar.globalAdminStats')}
                  </Button>
                </Col>
              }
              <Col md="2">
                <Button  className="" variant="outline-dark" href={AUTH_LOGOUT_URL}>{`${t('navbar.logout')}`}</Button>
              </Col>
            </Row>
          </Navbar.Collapse>
      </Navbar>
      <Container align="center">
        <hr />
        <Container align="left">
          <Row>
            <Col md="1"><span>{t("common.client")}:</span></Col>
            <Col><h5>{auth.getUsernameAndClient(username).client}</h5></Col>
          </Row>
          <Row>
            <Col md="1"><span>{t("common.user")}:</span></Col>
            <Col><h5>{auth.getUsernameAndClient(username).name}</h5></Col>
          </Row>
          {resources.svnroot_db_total_disk_usage && <Row>
            <Col md="2"><span>{t("common.diskUsage")}:</span></Col>
            <Col>
              <Row>
                <h5 className="mr-2">
                  {convertUnits(resources.svnroot_db_total_disk_usage)}
                </h5>
                <OverlayTrigger
                  placement="right"
                  overlay={popover("diskUsage")}
                >
                  <Button
                    className={usersAndRightsHelpButtonClass}
                    variant="primary"
                  >
                    ?
                  </Button>
                </OverlayTrigger>
              </Row>
            </Col>
          </Row>}
        </Container>
        <hr />
        <InputGroup className="mb-3">
          <FormControl
            onChange={e => setInputValue(e.target.value)}
            value={inputValue}
            placeholder={t('filters.searchPlaceholder')}
            aria-label="Filter"
            aria-describedby="basic-addon1"
            />
        </InputGroup>
        <Row className="mb-3">
          <Col md={6}>
            <Button className="lockedButton" onClick={() => setShowLocked(!showLocked)} variant="outline-dark">
              <FontAwesome name={!showLocked ? "unlock" : "lock"} />
              {` ${showLocked ? t('filters.showLocked') : t('filters.hideLocked')}`}
            </Button>
          </Col>
          <Col md={6}>
            <Button className="filtersFlagAllButton" onClick={removeFilters} variant="outline-info">{t('filters.removeFilters')}</Button>
          </Col>
        </Row>
      </Container>
      <div
        className="users-and-rights-container"
     >
        <div className="users-and-rights">
          <MultiselectFilter
            name={t('multiselect.users')}
            dropdownData={dropdownUsers}
            multiselectedData={multiselectedUsers}
            setMultiselectedData={setMultiselectedUsers}
          />
          <div className={isEmpty(multiselectedUsers) ? 'disabled-multiselect' : ''}>
            <MultiselectFilter
              name={t('multiselect.rights')}
              dropdownData={dropdownRights}
              multiselectedData={multiselectedRights}
              setMultiselectedData={setMultiselectedRights}
            />
          </div>
        </div>
        <OverlayTrigger placement="right" overlay={popover("userRights")}>
          <Button className={usersAndRightsHelpButtonClass} variant="primary">?</Button>
        </OverlayTrigger>
      </div>
      <div className="projects-table projects-table-filters">
        <ListGroup>
          { selectedFilters ? <h2>{t('projectsTable.filters')}</h2> : ''}
          {showFilters('multiselect.users', multiselectedUsers)}
          {showFilters('multiselect.rights', multiselectedRights)}
          {showFilters('projectsTable.status', multiselectedStatus)}
          {showFilters('projectsTable.type', multiselectedType)}
          {showFilters('projectsTable.leader', multiselectedLeader)}
          {showFilters('projectsTable.year', multiselectedYear)}
          {showFilters('dates.startDate', [convertDate(selectedDate.startDate)])}
          {showFilters('dates.endDate', [convertDate(selectedDate.endDate)])}
        </ListGroup>
      </div>
      {
        lastUpdateInfo.is_generating && <div className="container generation-in-progress text-danger">
          {t('common.generationInProgress')}
        </div>
      }
      <div className="projects-table">
        <h2>{t('projectsTable.title')}</h2>
        <ProjectsTable
            headers={headers}
            rows={projects}
            changeSort={changeSortOrder}
            sort={sortOrder}
            selectedProjects={selectedProjects}
            selectProject={selectProject}
            deselectProject={deselectProject}
            selectAllProjects={selectAllProjects}
            deselectAllProjects={deselectAllProjects}
            sortMethod={sortMethod}
            sortMethods={sortMethods}
            setSortMethod={setSortMethod}
        />
      </div>
      <hr/>
      <Row className="justify-content-center">
        <MultiselectFilter
          name={t('selectStatistics.dropdownTitle')}
          dropdownData={Object.values(STATISTICS)}
          multiselectedData={statisticsSelection}
          setMultiselectedData={setStatisticsSelection}
          buttonVariant='secondary'
        />
      </Row>
      {statisticsSelection.includes(STATISTICS.byUser) || statisticsSelection.includes(STATISTICS.byProject) ?
        <Row className="users-and-rights-container">
          <Row className="users-and-rights">
            <MultiselectFilter
              name={t('selectStatistics.selectUsers')}
              dropdownData={dropdownUsers}
              multiselectedData={statisticsUsers}
              setMultiselectedData={setStatisticsUsers}
              buttonVariant='outline '
            />
            <DateFilter
              name={t('selectStatistics.selectDate')}
              selectedDate={statisticsDate}
              setSelectedDate={setStatisticsDate}
              buttonVariant='outline'
            />
          </Row>
        </Row> :
        <></>
      }
      {
        statisticsSelection.includes(STATISTICS.currentStep) &&
        <div className="current-steps-statistics-table">
          <h2>{STATISTICS.currentStep}</h2>
          {
            isEmpty(projectsForStatistics) ?
            <>{t('statisticsTable.noProjectsSelected')}</> :
            <CurrentStepsStatisticsScreen
              statistics={currentStepsStatistics}
              projects={projectsForStatistics}
            />
          }
        </div>
      }
      {(statisticsSelection.includes(STATISTICS.byUser) ||
        statisticsSelection.includes(STATISTICS.byProject)) &&
        statisticsDate.startDate && statisticsDate.endDate ?
        <Row className="justify-content-center">
          <Col md={6}>
            <br/>
            <h4 className="statistics-date">{`Date: ${convertDate(statisticsDate.startDate)} - ${convertDate(statisticsDate.endDate)}`}</h4>
          </Col>
        </Row> :
        <></>
      } 
      {
        statisticsSelection.includes(STATISTICS.byUser) &&
        <div className="current-steps-statistics-table">
          <div className="current-steps-statistics-table-title">
            <h3>{STATISTICS.byUser}</h3>
            <OverlayTrigger placement="right" overlay={popover("statsUserAndProject")}>
              <Button className={usersAndRightsHelpButtonClass} variant="primary">?</Button>
            </OverlayTrigger>
          </div>
          {
            isEmpty(projectsForStatistics) ?
              <>{t('statisticsTable.noProjectsSelected')}</> :
              statisticsUsers.length ?
                <StatisticsByUser
                  multiselectedUsers={statisticsUsers}
                  projectsForStatistics={projectsForStatistics}
                  selectedDate={statisticsDate}
                /> :
                <>{t('statisticsTable.noUserSelected')}</>
          }
        </div>
      }
      {
        statisticsSelection.includes(STATISTICS.byProject) &&
        <div className="current-steps-statistics-table">
          <div className="current-steps-statistics-table-title">
            <h3>{STATISTICS.byProject}</h3>
            <OverlayTrigger placement="right" overlay={popover("statsUserAndProject")}>
              <Button className={usersAndRightsHelpButtonClass} variant="primary">?</Button>
            </OverlayTrigger>
          </div>
          {
            isEmpty(projectsForStatistics) ?
              <>{t('statisticsTable.noProjectsSelected')}</> :
              statisticsUsers.length ?
                <StatisticsByProject
                  multiselectedUsers={statisticsUsers}
                  projectsForStatistics={projectsForStatistics}
                  selectedDate={statisticsDate}
                /> :
                <>{t('statisticsTable.noUserSelected')}</>
          }
        </div>
      }
      {
        statisticsSelection.includes(STATISTICS.byResource) &&
        <div className="current-steps-statistics-table">
          <h3>{STATISTICS.byResource}</h3>
          {
            isEmpty(projectsForStatistics) ?
              <>{t('statisticsTable.noProjectsSelected')}</> :
              <ResourcesByProject
                projectsForStatistics={projectsForStatistics}
              />
          }
        </div>
      }
      {statisticsSelection.includes(STATISTICS.general) ?
          <>
          <Row className="users-and-rights-container">
            <h3>{STATISTICS.general}</h3>
          </Row>
          <Row className="users-and-rights-container">
            <Row className="users-and-rights">
              <DateFilter
                  name={t('selectStatistics.selectDate')}
                  selectedDate={adminStatsDate}
                  setSelectedDate={setAdminStatsDate}
                  buttonVariant='outline'
              />
            </Row>
          </Row></> :
          <></>
      }
      {
        (statisticsSelection.includes(STATISTICS.general) && adminStatsDate.startDate && adminStatsDate.endDate ?
          <Row className="justify-content-center">
            <Col md={6}>
              <br/>
              <h4 className="statistics-date">
                {`Date: ${convertDate(adminStatsDate.startDate)} - ${convertDate(adminStatsDate.endDate)}`}
              </h4>
            </Col>
          </Row> :
          <></>
        )
      }
      {
        statisticsSelection.includes(STATISTICS.general) && (
          <Container align="left" className="pt-4">
            <hr />
            <Row>
              <Col md="2">
                <span>{t("common.diskUsage")}:</span>
              </Col>
              <Col>
                <Row>
                  <h5 className="mr-2">
                    {convertUnits(resources.svnroot_db_total_disk_usage)}
                  </h5>
                  <OverlayTrigger
                    placement="right"
                    overlay={popover("diskUsage")}
                  >
                    <Button
                      className={usersAndRightsHelpButtonClass}
                      variant="primary"
                    >
                      ?
                    </Button>
                  </OverlayTrigger>
                </Row>
              </Col>
            </Row>
            <hr />
            <div className="current-steps-statistics-table">
              <AdminStatisticsScreen
                  showStats
                  showStatsWithTimePeriod
                  statistics={adminStatistics}
                  statsWithTimePeriod={adminStatisticsTimePeriod}
              />
            </div>
          </Container>
        )
      }
      {
        statisticsSelection.includes(STATISTICS.trt) && (
          <div>
            <Row className="users-and-rights-container mt-3">
              <h3>{STATISTICS.trt}</h3>
            </Row>
            <Row className="users-and-rights-container mb-5">
              <Row className="users-and-rights">
                <DateFilter
                    name={t('selectStatistics.selectDate')}
                    selectedDate={trtDate}
                    setSelectedDate={setTrtDate}
                    buttonVariant='outline'
                />
              </Row>
            </Row>
            {
              (statisticsSelection.includes(STATISTICS.trt) && trtDate.startDate && trtDate.endDate ?
                <Row className="justify-content-center">
                  <Col md={6}>
                    <br/>
                    <h4 className="statistics-date">
                      {`Date: ${convertDate(trtDate.startDate)} - ${convertDate(trtDate.endDate)}`}
                    </h4>
                  </Col>
                </Row> : <></>
              )
            }
            <Row className="justify-content-center mt-3 mb-5">
              <Button
                  variant="outline-dark"
                  onClick={() => {
                    const hasTrtDate = trtDate.startDate && trtDate.endDate;
                    const fileName = hasTrtDate ? trtInfoFileNameWithTimePeriod(trtDate.startDate, trtDate.endDate) : trtInfoFileName;
                    excel.exportToExcel(
                        trtExcelData,
                        fileName,
                    )
                  }}
              >
                {t('selectStatistics.trtInfo.downloadExcelImportsInfo')}
              </Button>
            </Row>
          </div>
        )
      }
      <hr/>
      <Container>
        <Row>
          <Col className="lastUpdateInfoCol" md={{ span: 5, offset: 4 }}>
            {t('footer.revision')}: {lastUpdateInfo.revision} | {t('footer.lastUpdated')}: {lastUpdateInfo.last_update}
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default MainScreen;
